import { createFileRoute } from "@tanstack/react-router";
import { SpokePage, buildSpokeHead, type SpokePageConfig } from "@/components/SpokePage";

const CONFIG: SpokePageConfig = {
  slug: "pressure-washing-web-design-colorado",
  metaTitle: "Pressure Washing Web Design in Colorado | Websites That Book Jobs",
  metaDescription:
    "Custom pressure washing web design across Colorado — fast, mobile-first, conversion-tested sites built to turn local searches into booked work. Owner-led. Call (719) 419-3935.",
  breadcrumbs: [
    { label: "Home", to: "/" },
    { label: "Colorado Springs digital marketing", to: "/colorado-springs" },
  ],
  currentLabel: "Pressure Washing Web Design Colorado",
  eyebrow: "Pressure Washing Web Design — Colorado",
  h1: (
    <>
      Pressure Washing <span className="text-brand-gold">Web Design</span> in Colorado
    </>
  ),
  heroDescription:
    "Hand-coded pressure washing websites that load in under 2 seconds, convert mobile searches, and rank from day one.",
  intro:
    "Pressure Washing websites lose the lead in the first 4 seconds on a phone. We hand-code fast, mobile-first pressure washing sites with sticky CTAs, dedicated service pages, and conversion-tested intake flows. No bloated templates, no page builders — just a fast custom site that turns local searches into booked work.",
  definition: {
    term: "Pressure Washing web design in Colorado",
    body: "Pressure Washing web design is the practice of building conversion-focused, mobile-first websites for pressure washing businesses — with dedicated service pages, clear CTAs, fast load times, and proper local SEO structure baked in from day one.",
  },
  whatWeDo: [
    { title: "Hand-coded for speed", body: "Sub-2-second mobile load with instant quote calculator above the fold." },
    { title: "Service pages per surface", body: "House wash, driveway, deck, roof soft-wash, commercial, fleet — each ranks independently." },
    { title: "Sq-ft quote calculator", body: "Square footage calculator that quotes and books in 60 seconds." },
    { title: "Before/after galleries", body: "Lazy-loaded gallery with image schema." },
    { title: "Recurring plan signup", body: "Annual house wash autopay above the fold drives LTV." },
    { title: "Schema and SEO at launch", body: "LocalBusiness, Service, FAQPage, Review schema baked in." },
  ],
  whyThisMarket: [
    { heading: "Surface-type pages outrank generic pressure washing", body: "House wash, driveway, and soft-wash roof are different prices, different pages." },
    { heading: "Instant pricing wins the booking", body: "Sq-ft calculators close the booking before competitors call back." },
    { heading: "Recurring annual washes drive LTV", body: "Annual autopay signup above the fold drives 3x customer lifetime value." },
  ],
  relatedLinks: [
    { to: "/colorado-springs", label: "Colorado Springs digital marketing hub" },
    { to: "/colorado-springs-web-design", label: "How our web design process works" },
    { to: "/web-design-pricing", label: "Web design pricing" },
    { to: "/colorado-springs", label: "Colorado Springs digital marketing agency" },
    { to: "/web-design-denver-colorado", label: "Denver web design" },
    { to: "/case-studies", label: "Colorado case studies" },
    { to: "/web-design", label: "Our web design service (all industries)" },
    { to: "/pricing", label: "Web design pricing" },
  ],
  faqs: [
    { q: "How much does a pressure washing website cost in Colorado?", a: "Website packages start at $1,850 one-time. All include one year of hosting and a built-in traffic dashboard. Extra pages are $85 each. Compare all packages or build a plan on our pricing page." },
    { q: "How long does it take to build a pressure washing website?", a: "Typical timeline is 4–6 weeks for a 12–20 page pressure washing site with full service and service-area pages. Larger multi-location pressure washing businesses can take 8–10 weeks." },
    { q: "Will the new site rank from day one?", a: "Existing rankings stay or improve because we build proper 301 redirects from every old URL, keep your domain, and ship with full schema and on-page SEO from launch — not as a phase 2 afterthought." },
    { q: "Do you only serve Colorado Springs pressure washing businesses?", a: "No — we build pressure washing sites for businesses across all of Colorado: Denver, Fort Collins, Pueblo, Castle Rock, Parker, Boulder, and the Front Range." },
  ],
  serviceName: "Pressure Washing Web Design in Colorado",
  serviceDescription:
    "Hand-coded, mobile-first websites for pressure washing businesses across Colorado. Fast, conversion-focused, and SEO-ready from launch.",
  areaServed: "Colorado",
};

export const Route = createFileRoute("/pressure-washing-web-design-colorado")({
  head: () => buildSpokeHead(CONFIG),
  component: () => <SpokePage config={CONFIG} />,
});
